import classNames from 'classnames';
import type { Stage as KonvaStage } from 'konva/lib/Stage';
import React, { useRef, useState, useCallback, useMemo } from 'react';

import { KonvaImagePromisesProvider } from 'common/components/RewindModal/Konvas/KonvaImagePromisesProvider';
import ShareableScreenPodium from 'common/components/RewindModal/Konvas/ShareableScreenPodium';
import Slides from 'common/components/RewindModal/Slides';

import styles from '../styles.module.scss';
import BottomActions from './component/BottomActions';
import Card from './component/Card';

type ScreenPodiumProps = {
  data: any;
  entities: any;
  onNext: () => void;
  isLastStep: boolean;
  screenName: ScreenNames;
  allScreens: any[];
  step: number;
  steps: number;
  onStep: (step: number) => void;
  trackingScreenName?: string;
};

const ScreenPodiumContent = ({
  data,
  entities,
  onNext,
  isLastStep,
  screenName,
  allScreens,
  isDownloadReady
}: ScreenPodiumProps & { isDownloadReady: boolean }) => {
  const stageRef = useRef<KonvaStage>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const flashbackType = useMemo(() => {
    if (!allScreens) return undefined;
    const flashbackScreen = allScreens.find(s => s.name === 'flashback_type');
    return flashbackScreen?.data?.flashback_type;
  }, [allScreens]);

  const isSeries = useMemo(
    () =>
      screenName.includes('series') ||
      screenName.startsWith('screen_top_series_online'),
    [screenName]
  );
  const topItems = useMemo(() => {
    const rankedContent = isSeries ? data?.series : data?.movies;
    const contentEntities = entities?.[0]?.entities ?? [];
    if (!rankedContent) return [];

    const validEntities = contentEntities.filter(
      (entity: any) => entity && entity.id
    );

    return rankedContent
      .slice(0, 3)
      .map((item: any, index: number) => {
        const itemData = validEntities.find(
          (entity: any) => entity.id === item.gid_entity
        );
        if (!itemData) return null;
        return { ...itemData, position: index + 1 };
      })
      .filter(Boolean);
  }, [data, entities, isSeries]);

  const { title, subtitle, shareText, className } = useMemo(() => {
    if (
      screenName.startsWith('screen_top_theater_') ||
      screenName.startsWith('screen_top_series_online')
    ) {
      return {
        title: isSeries ? 'Top séries' : 'Top Ciné',
        subtitle: isSeries
          ? 'Fraîchement sorties en streaming cette année, déjà cultes à vos yeux'
          : 'Fraîchement sortis en salle cette année, déjà cultes à vos yeux',
        shareText: isSeries
          ? 'Partager mon top séries'
          : 'Partager mon top films',
        className: styles.centeredLayout
      };
    }

    if (
      screenName === 'screen_movie_top3' ||
      screenName === 'screen_series_top3'
    ) {
      const isType6 = flashbackType === 6;
      return {
        title: isType6
          ? isSeries
            ? 'Top 3 séries Allociné'
            : 'Top 3 films Allociné'
          : isSeries
            ? 'Mon top séries'
            : 'Mon top films',
        subtitle: isType6
          ? isSeries
            ? "Les 3 séries les mieux notées de l'année."
            : "Les 3 films les mieux notés de l'année."
          : isSeries
            ? "Vos 3 séries les mieux notées de l'année."
            : "Vos 3 films les mieux notés de l'année.",
        shareText: isType6
          ? isSeries
            ? 'Partager le top 3 séries'
            : 'Partager le top 3 films'
          : isSeries
            ? 'Partager mon podium séries'
            : 'Partager mon podium films',
        className: styles.topLayout
      };
    }

    return {
      title: 'Votre Podium',
      subtitle: '',
      shareText: 'Partager'
    };
  }, [screenName, isSeries, flashbackType]);

  const handleDownload = useCallback(async () => {
    if (!isDownloadReady || isDownloading || !stageRef.current) {
      return;
    }

    setIsDownloading(true);

    setTimeout(() => {
      try {
        const stage = stageRef.current;
        if (!stage) {
          throw new Error('Konva stage is not available.');
        }

        const dataURL = stage.toDataURL({ pixelRatio: 2 });

        const link = document.createElement('a');
        link.download = `mon-podium-allocine-2025.png`;
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        // Silently handle download errors
        // eslint-disable-next-line no-console
        console.error('Error generating image:', error);
      } finally {
        setIsDownloading(false);
      }
    }, 100);
  }, [isDownloadReady, isDownloading]);

  return (
    <>
      <Slides title={title} subtitle={subtitle}>
        <div className={classNames(styles.contentLayout, className)}>
          <div
            className={classNames(
              styles.topCardsLayout,
              `top-cards-${topItems.length}`
            )}
          >
            {topItems.map((item: any, index: number) => (
              <div
                key={item.id}
                className={classNames(styles.topCardItem, `card-${index + 1}`)}
              >
                <Card path={item.poster?.path} />
                <div className={styles.topCardRank}>
                  <span className={styles.topCardRankNum}>{index + 1}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <BottomActions
          showShare={true}
          onShareClick={handleDownload}
          isShareDisabled={!isDownloadReady || isDownloading}
          shareText={isDownloading ? 'Génération...' : shareText}
          showNext={!isLastStep}
          onNextClick={onNext}
        />
      </Slides>

      <ShareableScreenPodium
        ref={stageRef}
        data={{
          title,
          subtitle:
            screenName === 'screen_top_theater_movies_2025' ||
            screenName === 'screen_top_series_online_2025'
              ? subtitle
              : undefined
        }}
        items={topItems}
        isSeries={isSeries}
      />
    </>
  );
};

const ScreenPodium = (props: ScreenPodiumProps) => {
  const [isDownloadReady, setIsDownloadReady] = useState(false);

  return (
    <KonvaImagePromisesProvider onDownloadableChange={setIsDownloadReady}>
      <ScreenPodiumContent {...props} isDownloadReady={isDownloadReady} />
    </KonvaImagePromisesProvider>
  );
};

export default ScreenPodium;
